import { Link } from "react-router-dom";
import { UnderlinedTag } from "./UnderlinedTag";
import { getThemeForPath } from "../config/theme";
import { getColorForProject, getCategoryForPath, getColorForCategory } from "../config/projects";

/**
 * Unified project link component for consistent styling across all pages
 * Supports colored underlines, hover effects, and auto-detects theme colors
 * 
 * @param {string} to - Route path for the project
 * @param {string} title - Project title (optional if project exists in config)
 * @param {React.Component} icon - Icon component (optional if project exists in config)
 * @param {string|null} colorClass - Override theme color (auto-detected from project config or route if not provided)
 * @param {string|null} iconColor - Icon color class (defaults to matching colorClass)
 * @param {boolean} isCustomIcon - Whether icon uses custom props (e.g., PoolIcon uses 'classes' instead of 'className')
 */
export const ProjectLink = ({ 
    to, 
    title = null, 
    icon: IconComponent = null, 
    colorClass = null, 
    iconColor = null,
    isCustomIcon = false 
}) => {
    // Try to get color from centralized project config first, then fall back to route theme
    // For dynamic routes, use hierarchy-based category detection
    const projectColor = getColorForProject(to);
    const category = getCategoryForPath(to);
    const categoryColor = category ? getColorForCategory(category) : null;
    const routeColor = getThemeForPath(`/${to}`);
    const themeColor = colorClass || projectColor || categoryColor || routeColor || 'bg-badger-green';
    const iconColorClass = iconColor || themeColor.replace('bg-', 'text-');
    
    // Color map for hover background
    const colorMap = {
        'bg-badger-green': 'rgba(98, 168, 124, 0.3)',
        'bg-badger-blue': 'rgba(47, 151, 193, 0.3)',
        'bg-badger-purple': 'rgba(162, 110, 204, 0.3)',
        'bg-badger-red': 'rgba(214, 64, 69, 0.3)',
        'bg-badger-orange': 'rgba(245, 138, 7, 0.3)',
        'bg-badger-yellow': 'rgba(241, 196, 15, 0.3)',
    };
    const hoverBgColor = colorMap[themeColor] || colorMap['bg-badger-green'];
    
    return (
        <Link
            to={to}
            className="group sm:w-auto w-full border border-neutral-300 p-4 shadow rounded-lg lg:mx-0 mt-4 sm:mt-6 flex items-center cursor-pointer hover:shadow-lg transition-all duration-200 relative overflow-hidden"
            onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = hoverBgColor;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
            }}
        >
            {isCustomIcon ? (
                <IconComponent classes={`h-8 w-8 mr-3 flex-shrink-0 ${iconColorClass} transition-transform duration-200 group-hover:scale-110`} />
            ) : (
                <IconComponent className={`h-8 w-8 mr-3 flex-shrink-0 ${iconColorClass} transition-transform duration-200 group-hover:scale-110`} />
            )}
            <span className="flex-1 text-center font-medium">
                <UnderlinedTag text={title} colorClass={themeColor} enableHover={true} />
            </span>
        </Link>
    );
};

